import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import {
  callBanner,
  TargetParams,
  WindowWithAdriver,
} from '@/components/Banners/adriver';
import { useBannerStore } from '@/store/useBannerStore/useBannerStore';
import { handleAndLoadBanner } from '@/components/Banners/AdriverBanner.helpers';
import { BannerName } from '@/components/Banners/Banners.types';
import { AdriverBannerId } from '@/components/Banners/Banners.constants';
import {
  BannerSource,
  SetBannerSource,
} from '@/store/useBannerStore/useBannerStore.types';
import { sleep } from '@/utils/helpers';

interface BannerProps {
  containerId: string;
  containerClassName?: string;
  tagName: string;
  sid: string;
  bn: string;
  bt?: string;
  arCn?: number;
  targetParams?: TargetParams;
  isAjax?: boolean;
}

export const AdriverBanner: React.FC<BannerProps> = ({
  containerId,
  containerClassName,
  tagName,
  sid,
  bn,
  bt = '43',
  arCn,
  targetParams,
  isAjax,
}) => {
  const router = useRouter();
  const tag = tagName || 'div';

  const [
    isBlockerDetected,
    bannerTopSource,
    bannerBottomSource,
    bannerBackgroundSource,
    setBannerTopSource,
    setBannerBottomSource,
    setBannerBackgroundSource,
    setIsAdriverTopBannerVisible,
  ] = useBannerStore(state => [
    state.isBlockerDetected,
    state.bannerTopSource,
    state.bannerBottomSource,
    state.bannerBackgroundSource,
    state.setBannerTopSource,
    state.setBannerBottomSource,
    state.setBannerBackgroundSource,
    state.setIsAdriverTopBannerVisible,
  ]);

  function handleBannerLoadState(
    bannerType: keyof typeof AdriverBannerId,
    loaded: boolean
  ) {
    if (bannerType === AdriverBannerId[BannerName.Top]) {
      setIsAdriverTopBannerVisible(loaded);
    }
  }

  async function checkBanner(
    selector: string,
    setBannerSource: SetBannerSource
  ) {
    await sleep(5_000);

    const banner = document.querySelector(selector);
    const isBannerLoaded = !!banner?.clientHeight && banner.clientHeight > 0;

    if (!isBannerLoaded) {
      setBannerSource(BannerSource.Admanager);
    }
  }

  useEffect(() => {
    const bannerRefreshHandler = (currentPath: string) => {
      if (currentPath === '/' && !isBlockerDetected) {
        callBanner({
          container_id: containerId,
          sid,
          bn,
          bt,
          arCn,
          target: targetParams,
          isAjax,
        });
      } else {
        typeof (window as WindowWithAdriver).adriverExtentionLoad !==
          'undefined' &&
          (window as WindowWithAdriver).adriverExtentionLoad?.(
            0,
            null,
            containerId
          );
      }
    };

    if (!isBlockerDetected) {
      callBanner({
        container_id: containerId,
        sid,
        bn,
        bt,
        arCn,
        target: targetParams,
        isAjax,
      });
    } else {
      typeof (window as WindowWithAdriver).adriverExtentionLoad !==
        'undefined' &&
        (window as WindowWithAdriver).adriverExtentionLoad?.(
          0,
          null,
          containerId
        );
    }

    router.events.on('routeChangeComplete', bannerRefreshHandler);

    return () => {
      router.events.off('routeChangeComplete', bannerRefreshHandler);
    };
  }, []);

  useEffect(() => {
    if (containerId === AdriverBannerId[BannerName.Top]) {
      setIsAdriverTopBannerVisible(false);

      handleAndLoadBanner(
        BannerName.Top,
        setBannerTopSource,
        bannerTopSource === BannerSource.Admanager,
        handleBannerLoadState
      );
      checkBanner('.lotriver-top-banner', setBannerTopSource);
    }

    if (containerId === AdriverBannerId[BannerName.Bottom]) {
      handleAndLoadBanner(
        BannerName.Bottom,
        setBannerBottomSource,
        bannerBottomSource === BannerSource.Admanager,
        handleBannerLoadState
      );
      checkBanner('.lotriver-bottom-banner--in-list', setBannerBottomSource);
    }

    if (containerId === AdriverBannerId[BannerName.Background]) {
      handleAndLoadBanner(
        BannerName.Background,
        setBannerBackgroundSource,
        bannerBackgroundSource === BannerSource.Admanager,
        handleBannerLoadState
      );
      checkBanner('.lotriver-bg-left', setBannerBackgroundSource);
    }
  }, [router.pathname]);

  return React.createElement(
    tag,
    { id: containerId, className: containerClassName },
    null
  );
};
